import { motion } from "framer-motion";
import { NOXN } from "@/constants/testIds";
import { MapPin, ArrowUpRight, CalendarCheck2, Radar } from "lucide-react";

const LED_IMG = "https://static.prod-images.emergentagent.com/jobs/7a9b6db3-da51-486d-b381-a72900b8fc1d/images/19ea8d617b2e136a2c645cfe754236ae11eb7d5869067c113834236e2308726f.png";
const CITY_IMG = "https://images.unsplash.com/photo-1760042001943-8be600eb252f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODF8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjaXR5c2NhcGUlMjBuaWdodCUyMG5lb24lMjBvcmFuZ2V8ZW58MHx8fHwxNzgwMjIzNTk5fDA&ixlib=rb-4.1.0&q=85";

const fadeIn = (delay = 0) => ({
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] },
});

export const Services = ({ onBegin }) => {
    return (
        <section
            id="services"
            data-testid={NOXN.servicesSection}
            className="relative py-14 lg:py-20"
        >
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div {...fadeIn(0)} className="max-w-3xl mb-12">
                    <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-[#FF5A00] mb-6 flex items-center gap-3">
                        <span className="inline-block w-6 h-px bg-[#FF5A00]" />
                        02 / What you get
                    </div>
                    <h2 className="font-display font-black tracking-tighter text-white text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                        Get seen by the people<br />
                        <span className="text-slate-500">who walk past your business.</span>
                    </h2>
                </motion.div>

                {/* Bento grid */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                    {/* CARD 1 / Ad Space (large) */}
                    <motion.div
                        {...fadeIn(0.05)}
                        data-testid={NOXN.serviceCardSpace}
                        className="landing-hero-dark lg:col-span-7 lg:row-span-2 relative overflow-hidden rounded-3xl border border-[#FF5A00]/30 group hover:border-[#FF5A00]/70 transition-colors duration-500 min-h-[480px] card-lift noxn-shimmer shadow-lg shadow-[#1E3A6F]/10"
                        style={{
                            background:
                                "linear-gradient(160deg, #2B4680 0%, #1E3A6F 45%, #16305C 100%)",
                        }}
                    >
                        <div className="absolute inset-0 -z-0">
                            <img
                                src={LED_IMG}
                                alt="LED display texture"
                                className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity duration-700"
                                style={{ filter: "saturate(1.4) contrast(1.05)" }}
                            />
                            <div className="absolute inset-0 mix-blend-color-dodge opacity-30"
                                 style={{ background: "linear-gradient(135deg, rgba(255,90,0,0.4), rgba(255,90,0,0) 60%)" }} />
                            <div className="absolute inset-0"
                                 style={{ background: "linear-gradient(to top, rgba(10,10,20,0.92) 0%, rgba(10,15,25,0.55) 45%, rgba(10,15,25,0.15) 100%)" }} />
                            <div className="absolute inset-0 opacity-40"
                                 style={{
                                     backgroundImage: "radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px)",
                                     backgroundSize: "10px 10px",
                                 }} />
                        </div>

                        <div className="pointer-events-none absolute -top-20 -right-16 w-[360px] h-[360px] rounded-full opacity-70 blur-[80px]"
                             style={{ background: "radial-gradient(circle, rgba(255,90,0,0.55), rgba(255,90,0,0) 65%)" }} />

                        <div className="relative z-10 p-10 lg:p-12 h-full flex flex-col justify-between">
                            <div className="flex items-start justify-end">
                                <MapPin className="text-[#FF5A00]" size={22} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h3 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-white">
                                    Book billboards without the phone calls
                                </h3>
                                <p className="mt-4 text-slate-400 text-base lg:text-lg leading-relaxed max-w-lg">
                                    Premium digital billboards in Brisbane, Sydney and Melbourne
                                    — with live availability, transparent pricing, and a
                                    20-minute hold while you pay. No insertion orders, no lock-ins.
                                </p>
                                <ul className="mt-8 grid grid-cols-2 gap-x-6 gap-y-3 text-sm text-slate-300 max-w-md">
                                    {["Live availability", "Pay-per-play pricing", "Real-time checkout holds", "7-day run windows"].map((i) => (
                                        <li key={i} className="flex items-center gap-2">
                                            <span className="w-1 h-1 bg-[#FF5A00]" />
                                            {i}
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    onClick={onBegin}
                                    data-testid="service-space-cta"
                                    className="mt-10 inline-flex items-center gap-2 text-sm font-mono-tech uppercase tracking-widest text-white border-b border-[#FF5A00] pb-1 hover:gap-3 transition-all"
                                >
                                    Put Me On A Billboard <ArrowUpRight size={16} />
                                </button>
                            </div>
                        </div>
                    </motion.div>

                    {/* CARD 2 / Live map */}
                    <motion.div
                        {...fadeIn(0.15)}
                        data-testid="service-card-live-map"
                        className="landing-hero-dark lg:col-span-5 relative overflow-hidden rounded-3xl border border-white/10 bg-[#1B2E55] group hover:border-[#FF5A00]/40 transition-colors duration-500 min-h-[300px] card-lift noxn-shimmer shadow-lg shadow-[#1E3A6F]/10"
                    >
                        <div className="absolute inset-0 -z-0">
                            <img src={CITY_IMG} alt="City billboards" className="w-full h-full object-cover opacity-40 group-hover:opacity-55 transition-opacity duration-700" />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#1B2E55] via-[#1B2E55]/70 to-transparent" />
                        </div>
                        <div className="relative z-10 p-10 lg:p-12 h-full flex flex-col justify-between">
                            <div className="flex items-start justify-end">
                                <Radar className="text-[#FF5A00]" size={22} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h3 className="font-display font-black tracking-tighter text-4xl lg:text-5xl text-white">
                                    See what&apos;s free this week
                                </h3>
                                <p className="mt-4 text-slate-400 text-base leading-relaxed max-w-md">
                                    An interactive map of every screen with real-time
                                    availability. Tap one to see the price per play and jump
                                    straight into the booking wizard.
                                </p>
                                <a href="/map" data-testid="service-map-cta"
                                    className="mt-6 inline-flex items-center gap-2 text-sm font-mono-tech uppercase tracking-widest text-white border-b border-[#FF5A00] pb-1 hover:gap-3 transition-all">
                                    Open the live map <ArrowUpRight size={16} />
                                </a>
                            </div>
                        </div>
                    </motion.div>

                    {/* CARD 3 / Coordination */}
                    <motion.div
                        {...fadeIn(0.25)}
                        data-testid="service-card-coordination"
                        className="lg:col-span-5 relative overflow-hidden rounded-3xl border border-white/10 bg-[#1B2E55] group hover:border-[#FF5A00]/40 transition-colors duration-500 min-h-[220px] grid grid-cols-1 sm:grid-cols-5 card-lift noxn-shimmer shadow-lg shadow-[#1E3A6F]/10"
                    >
                        <div className="relative sm:col-span-2 min-h-[200px] overflow-hidden">
                            <img
                                src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?auto=format&fit=crop&w=900&q=80"
                                alt="Campaign coordinator reviewing a digital billboard plan"
                                className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity duration-700"
                                loading="lazy"
                            />
                            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#1B2E55]/30 to-[#1B2E55] sm:bg-gradient-to-r sm:from-transparent sm:to-[#1B2E55]" />
                        </div>
                        <div className="sm:col-span-3 p-8 lg:p-10 flex flex-col justify-between">
                            <div className="font-mono-tech text-[11px] uppercase tracking-[0.35em] text-slate-400 mb-3">
                                / A real campaign director on your account
                            </div>
                            <div>
                                <h3 className="font-display font-black tracking-tighter text-2xl lg:text-3xl text-white leading-tight">
                                    <CalendarCheck2 size={20} className="inline-block text-[#FF5A00] mr-2 -mt-1" />
                                    Booking to go-live, managed
                                </h3>
                                <p className="mt-3 text-sm text-slate-300 leading-relaxed max-w-md">
                                    Screen scheduling, artwork spec checks and operator
                                    approvals — coordinated through one person, not a queue.
                                </p>
                                <a
                                    href="#process"
                                    data-testid="service-coord-cta"
                                    className="mt-5 inline-flex items-center gap-2 text-sm font-mono-tech uppercase tracking-widest text-white border-b border-[#FF5A00] pb-1 hover:gap-3 transition-all"
                                >
                                    See the four-step process <ArrowUpRight size={16} />
                                </a>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </div>
        </section>
    );
};
